import { Injectable, inject } from '@angular/core';
import { Firestore, doc, getDoc, updateDoc } from '@angular/fire/firestore';
import { HandleErrorService } from './log.service';
import { environment } from '../../environments/environment';

// ----------------------------------------------------------------------
// Base32 Encoding/Decoding (Native Implementation)
// ----------------------------------------------------------------------
class Base32 {
  private static readonly alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

  static decode(input: string): Uint8Array {
    const cleanInput = input.toUpperCase().replace(/=+$/, '');
    const length = cleanInput.length;
    const bits = length * 5;
    const bytes = Math.floor(bits / 8);
    const output = new Uint8Array(bytes);

    let value = 0;
    let bitsRemaining = 0;
    let index = 0;

    for (let i = 0; i < length; i++) {
      const char = cleanInput[i];
      const val = this.alphabet.indexOf(char);
      if (val === -1) continue;

      value = (value << 5) | val;
      bitsRemaining += 5;

      if (bitsRemaining >= 8) {
        output[index++] = (value >>> (bitsRemaining - 8)) & 0xff;
        bitsRemaining -= 8;
      }
    }
    return output;
  }

  static encode(input: Uint8Array): string {
    let output = '';
    let value = 0;
    let bitsRemaining = 0;

    for (let i = 0; i < input.length; i++) {
      value = (value << 8) | input[i];
      bitsRemaining += 8;

      while (bitsRemaining >= 5) {
        output += this.alphabet[(value >>> (bitsRemaining - 5)) & 0x1f];
        bitsRemaining -= 5;
      }
    }

    if (bitsRemaining > 0) {
      output += this.alphabet[(value << (5 - bitsRemaining)) & 0x1f];
    }

    return output;
  }
}

@Injectable({
  providedIn: 'root'
})
export class TwoFactorService {
  private firestore = inject(Firestore);
  private errorHandler = inject(HandleErrorService);

  // Rate limiting storage
  private verificationAttempts: Map<string, { count: number; resetTime: number }> = new Map();
  private readonly MAX_ATTEMPTS = 5; // Max attempts per 2-minute window
  private readonly ATTEMPT_WINDOW = 2 * 60 * 1000; // 2 minutes in milliseconds

  /**
   * Encrypts a 2FA secret for storage in Firestore.
   * 
   * ⚠️ SECURITY LIMITATION - CLIENT-SIDE ENCRYPTION:
   * 
   * This encryption key is stored in environment files and will be compiled into
   * the frontend JavaScript bundle. This means:
   * 
   * ✅ WHAT IT PROTECTS:
   * - Encryption at rest (secrets are encrypted in Firestore)
   * - Protection against database breaches (raw secrets not visible in DB)
   * - Defense in depth (multiple layers of security)
   * 
   * ❌ WHAT IT DOESN'T PROTECT:
   * - Anyone who inspects the compiled JS can extract the key
   * - Anyone with the key + database access can decrypt all secrets
   * - This is NOT true end-to-end encryption
   * 
   * 🔒 FOR TRUE SECURITY:
   * - Use Firebase Cloud Functions for server-side encryption
   * - Never expose the encryption key to the client
   * - Use Firebase App Check to verify requests come from your app
   * - Consider using Firebase's native multiFactor API (handles this securely)
   * 
   * This implementation provides "encryption at rest" but the key is client-accessible.
   * For production, consider migrating to server-side encryption via Cloud Functions.
   */
  private async encryptSecret(secret: string): Promise<string> {
    try {
      // Get encryption key from environment
      // WARNING: This key will be visible in compiled frontend code!
      // WARNING: Changing this key will break all existing encrypted secrets!
      const encryptionKey = environment.twoFactorEncryptionKey || '2FA_SECRET_KEY_32_BYTES_LONG!!';
      if (encryptionKey.length !== 32) {
        throw new Error('2FA encryption key must be exactly 32 bytes (32 characters)');
      }
      
      const keyMaterial = await window.crypto.subtle.importKey(
        'raw',
        new TextEncoder().encode(encryptionKey),
        { name: 'PBKDF2' },
        false,
        ['deriveBits', 'deriveKey']
      );

      const key = await window.crypto.subtle.deriveKey(
        {
          name: 'PBKDF2',
          salt: new TextEncoder().encode('2FA_SALT'),
          iterations: 100000,
          hash: 'SHA-256'
        },
        keyMaterial,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt']
      );

      const iv = window.crypto.getRandomValues(new Uint8Array(12));
      const encrypted = await window.crypto.subtle.encrypt(
        { name: 'AES-GCM', iv },
        key,
        new TextEncoder().encode(secret)
      );

      // Combine IV and encrypted data
      const combined = new Uint8Array(iv.length + encrypted.byteLength);
      combined.set(iv);
      combined.set(new Uint8Array(encrypted), iv.length);

      // Return as base64
      return btoa(String.fromCharCode(...combined));
    } catch (error) {
      this.errorHandler.log(error);
      // Fallback: return base64 encoded (not secure, but better than plain text)
      return btoa(secret);
    }
  }

  /**
   * Decrypts a 2FA secret from Firestore.
   * 
   * ⚠️ See encryptSecret() documentation for security limitations.
   * The decryption key is client-accessible and visible in compiled code.
   */
  private async decryptSecret(encryptedSecret: string): Promise<string> {
    try {
      const combined = Uint8Array.from(atob(encryptedSecret), c => c.charCodeAt(0));
      const iv = combined.slice(0, 12);
      const encrypted = combined.slice(12);

      // Get encryption key from environment
      // WARNING: This key is visible in compiled frontend code!
      // WARNING: Changing this key will break all existing encrypted secrets!
      const encryptionKey = environment.twoFactorEncryptionKey || '2FA_SECRET_KEY_32_BYTES_LONG!!';
      if (encryptionKey.length !== 32) {
        throw new Error('2FA encryption key must be exactly 32 bytes (32 characters)');
      }
      
      const keyMaterial = await window.crypto.subtle.importKey(
        'raw',
        new TextEncoder().encode(encryptionKey),
        { name: 'PBKDF2' },
        false,
        ['deriveBits', 'deriveKey']
      );

      const key = await window.crypto.subtle.deriveKey(
        {
          name: 'PBKDF2',
          salt: new TextEncoder().encode('2FA_SALT'),
          iterations: 100000,
          hash: 'SHA-256'
        },
        keyMaterial,
        { name: 'AES-GCM', length: 256 },
        false,
        ['decrypt']
      );

      const decrypted = await window.crypto.subtle.decrypt(
        { name: 'AES-GCM', iv },
        key,
        encrypted
      );

      return new TextDecoder().decode(decrypted);
    } catch (error) {
      this.errorHandler.log(error);
      // Fallback: try base64 decode
      try {
        return atob(encryptedSecret);
      } catch {
        return '';
      }
    }
  }

  /**
   * Generates a random Base32 secret key
   */
  generateSecret(length: number = 20): string {
    const randomBuffer = new Uint8Array(length);
    window.crypto.getRandomValues(randomBuffer);
    return Base32.encode(randomBuffer);
  }

  /**
   * Generates the otpauth URL for QR codes
   */
  generateOtpAuthUrl(secret: string, accountName: string, issuer: string): string {
    return `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(accountName)}?secret=${secret}&issuer=${encodeURIComponent(issuer)}&algorithm=SHA1&digits=6&period=30`;
  }

  /**
   * Verifies a TOTP token against a secret
   */
  async verify(token: string, secret: string, windowData: number = 1): Promise<boolean> {
    if (!token || !secret) return false;
    
    // Check current window and +/- windows for drift tolerance
    const currentCounter = Math.floor(Date.now() / 1000 / 30);
    
    for (let i = -windowData; i <= windowData; i++) {
      const generated = await this.generateToken(secret, currentCounter + i);
      if (generated === token) return true;
    }
    return false;
  }

  /**
   * Core TOTP generation logic using Web Crypto API
   */
  private async generateToken(secret: string, counter: number): Promise<string> {
    try {
      const keyBytes = Base32.decode(secret);
      
      // Convert counter to buffer (8 bytes big-endian)
      const counterBuffer = new ArrayBuffer(8);
      const counterView = new DataView(counterBuffer);
      counterView.setUint32(4, counter, false); // Low 32 bits
      counterView.setUint32(0, 0, false);       // High 32 bits (assumed 0 for reasonable dates)

      // Import key for HMAC-SHA1
      const key = await window.crypto.subtle.importKey(
        'raw',
        keyBytes,
        { name: 'HMAC', hash: { name: 'SHA-1' } },
        false,
        ['sign']
      );

      // Sign
      const signature = await window.crypto.subtle.sign('HMAC', key, counterBuffer);
      const signatureArray = new Uint8Array(signature);

      // Truncate
      const offset = signatureArray[signatureArray.length - 1] & 0xf;
      const binary =
        ((signatureArray[offset] & 0x7f) << 24) |
        ((signatureArray[offset + 1] & 0xff) << 16) |
        ((signatureArray[offset + 2] & 0xff) << 8) |
        (signatureArray[offset + 3] & 0xff);

      const otp = binary % 1000000;
      return otp.toString().padStart(6, '0');
    } catch (e) {
      this.errorHandler.log(e);
      return '';
    }
  }

  /**
   * Checks if user has 2FA enabled
   */
  async isTwoFactorEnabled(uid: string): Promise<boolean> {
    try {
      const docRef = doc(this.firestore, 'users', uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return data['twoFactorEnabled'] === true;
      }
      return false;
    } catch (error) {
      this.errorHandler.log(error);
      return false;
    }
  }

  /**
   * Enables 2FA for a user and stores encrypted secret
   */
  async enableTwoFactor(uid: string, secret: string): Promise<void> {
    try {
      const encryptedSecret = await this.encryptSecret(secret);
      const docRef = doc(this.firestore, 'users', uid);
      
      await updateDoc(docRef, {
        twoFactorEnabled: true,
        twoFactorSecret: encryptedSecret,
        twoFactorSetupDate: new Date()
      });
    } catch (error) {
      this.errorHandler.log(error);
      throw error;
    }
  }

  /**
   * Disables 2FA for a user (admin function)
   */
  async disableTwoFactor(uid: string): Promise<void> {
    try {
      const docRef = doc(this.firestore, 'users', uid);
      
      await updateDoc(docRef, {
        twoFactorEnabled: false,
        twoFactorSecret: null,
        twoFactorSetupDate: null
      });
    } catch (error) {
      this.errorHandler.log(error);
      throw error;
    }
  }

  /**
   * Gets and decrypts the user's TOTP secret for verification
   */
  async getTwoFactorSecret(uid: string): Promise<string | null> {
    try {
      const docRef = doc(this.firestore, 'users', uid);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const encryptedSecret = data['twoFactorSecret'];
        
        if (encryptedSecret) {
          return await this.decryptSecret(encryptedSecret);
        }
      }
      return null;
    } catch (error) {
      this.errorHandler.log(error);
      return null;
    }
  }

  /**
   * Checks rate limiting for verification attempts
   */
  private checkRateLimit(uid: string): boolean {
    const now = Date.now();
    const userAttempts = this.verificationAttempts.get(uid);

    if (!userAttempts || now > userAttempts.resetTime) {
      // Reset or initialize
      this.verificationAttempts.set(uid, {
        count: 0,
        resetTime: now + this.ATTEMPT_WINDOW
      });
      return true;
    }

    if (userAttempts.count >= this.MAX_ATTEMPTS) {
      return false; // Rate limit exceeded
    }

    return true;
  }

  /**
   * Records a verification attempt
   */
  private recordAttempt(uid: string): void {
    const userAttempts = this.verificationAttempts.get(uid);
    if (userAttempts) {
      userAttempts.count++;
    }
  }

  /**
   * Gets remaining attempts before lockout
   */
  getRemainingAttempts(uid: string): number {
    const userAttempts = this.verificationAttempts.get(uid);
    if (!userAttempts || Date.now() > userAttempts.resetTime) {
      return this.MAX_ATTEMPTS;
    }
    return Math.max(0, this.MAX_ATTEMPTS - userAttempts.count);
  }

  /**
   * Gets time until rate limit resets (in seconds)
   */
  getRateLimitResetTime(uid: string): number {
    const userAttempts = this.verificationAttempts.get(uid);
    if (!userAttempts || Date.now() > userAttempts.resetTime) {
      return 0;
    }
    return Math.ceil((userAttempts.resetTime - Date.now()) / 1000);
  }

  /**
   * Marks a user as verified in the current session
   */
  markSessionVerified(uid: string): void {
    try {
      const verifiedUsers = this.getVerifiedUsers();
      verifiedUsers.add(uid);
      sessionStorage.setItem('2fa_verified_users', JSON.stringify(Array.from(verifiedUsers)));
    } catch (error) {
      this.errorHandler.log(error);
    }
  }

  /**
   * Checks if a user is verified in the current session
   */
  isSessionVerified(uid: string): boolean {
    try {
      const verifiedUsers = this.getVerifiedUsers();
      return verifiedUsers.has(uid);
    } catch (error) {
      this.errorHandler.log(error);
      return false;
    }
  }

  /**
   * Clears session verification for a user (e.g., on logout)
   */
  clearSessionVerification(uid: string): void {
    try {
      const verifiedUsers = this.getVerifiedUsers();
      verifiedUsers.delete(uid);
      sessionStorage.setItem('2fa_verified_users', JSON.stringify(Array.from(verifiedUsers)));
    } catch (error) {
      this.errorHandler.log(error);
    }
  }

  /**
   * Gets the set of verified users from sessionStorage
   */
  private getVerifiedUsers(): Set<string> {
    try {
      const stored = sessionStorage.getItem('2fa_verified_users');
      if (stored) {
        return new Set(JSON.parse(stored));
      }
    } catch (error) {
      this.errorHandler.log(error);
    }
    return new Set();
  }

  /**
   * Verifies TOTP code for a user with rate limiting
   */
  async verifyUserTOTP(uid: string, code: string): Promise<{ success: boolean; error?: string }> {
    try {
      // Check rate limiting
      if (!this.checkRateLimit(uid)) {
        const resetTime = this.getRateLimitResetTime(uid);
        return {
          success: false,
          error: `Too many attempts. Please wait ${Math.ceil(resetTime / 60)} minutes before trying again.`
        };
      }

      const secret = await this.getTwoFactorSecret(uid);
      if (!secret) {
        this.recordAttempt(uid);
        return {
          success: false,
          error: '2FA is not set up for this account.'
        };
      }

      const isValid = await this.verify(code, secret);
      
      if (!isValid) {
        this.recordAttempt(uid);
      } else {
        // Reset attempts on success
        this.verificationAttempts.delete(uid);
        // Mark user as verified in this session
        this.markSessionVerified(uid);
      }

      return {
        success: isValid,
        error: isValid ? undefined : 'Invalid code. Please try again.'
      };
    } catch (error: any) {
      this.errorHandler.log(error);
      this.recordAttempt(uid);
      return {
        success: false,
        error: error.message || 'An error occurred during verification.'
      };
    }
  }
}

